/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import {ResponseData} from '@dvrd/dvr-controls';

import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {
    OverviewPeriodRange,
    OverviewTopStatsAds,
    OverviewTopStatsGeneral,
    OverviewTopStatsProfit
} from '../../../util/interfaces';

import {buildStatCards} from '../../../util/overviewUtil';

import {
    getOverviewTopStatsAds,
    getOverviewTopStatsGeneral,
    getOverviewTopStatsProfit
} from '../../../util/requestSender';

import DashboardKpi from './dashboardKpi';

interface Props {
    accountIds: Array<string>;
    period: OverviewPeriodRange;
    comparisonLabel: string;
    hideComparison: boolean;
}

interface BlockState<T> {
    data: T | null;
    loading: boolean;
    error: boolean;
}

function initialBlock<T>(loading: boolean): BlockState<T> {
    return {data: null, loading, error: false};
}

export default function DashboardKpiController({accountIds, period, comparisonLabel, hideComparison}: Props) {
    const {t} = useTranslation();
    const [general, setGeneral] = useState<BlockState<OverviewTopStatsGeneral>>(initialBlock(accountIds.length > 0));
    const [profit, setProfit] = useState<BlockState<OverviewTopStatsProfit>>(initialBlock(accountIds.length > 0));
    const [ads, setAds] = useState<BlockState<OverviewTopStatsAds>>(initialBlock(accountIds.length > 0));
    const accountKey = accountIds.join(',');
    const periodKey = `${period.start_date}|${period.end_date}`;
    // One counter for all three: a period change supersedes every in-flight
    // block at once, so a slow profit response cannot land next to fresh
    // general numbers.
    const requestSeq = useRef(0);

    // Profit and ads render as soon as they arrive; until then their cards are
    // simply absent rather than blocking the fast general block.
    const cards = useMemo(
        () => buildStatCards(general.data, profit.data, ads.data, t),
        [general.data, profit.data, ads.data, t]
    );

    const loading = general.loading || profit.loading || ads.loading;
    // Only a total failure is an error state — one dead block still leaves a
    // useful page, and its cards just do not appear.
    const error = general.error && profit.error && ads.error;

    function loadBlock<T>(
        request: (params: {
            data: {account_ids: Array<string>; period: OverviewPeriodRange};
            callback: (data: ResponseData) => void;
            errorCallback: () => void;
        }) => void,
        setState: (state: BlockState<T>) => void,
        seq: number
    ) {
        request({
            data: {account_ids: accountIds, period},
            callback: (data: ResponseData) => {
                if (seq !== requestSeq.current) return;
                if (data.handled) return setState({data: null, loading: false, error: false});
                if (data.success === false || !data.values) return setState({data: null, loading: false, error: true});
                setState({data: {values: data.values, previous: data.previous} as T, loading: false, error: false});
            },
            errorCallback: () => {
                if (seq !== requestSeq.current) return;
                setState({data: null, loading: false, error: true});
            }
        });
    }

    function loadStats() {
        requestSeq.current += 1;
        const seq = requestSeq.current;
        setGeneral(initialBlock(true));
        setProfit(initialBlock(true));
        setAds(initialBlock(true));
        loadBlock<OverviewTopStatsGeneral>(getOverviewTopStatsGeneral, setGeneral, seq);
        loadBlock<OverviewTopStatsProfit>(getOverviewTopStatsProfit, setProfit, seq);
        loadBlock<OverviewTopStatsAds>(getOverviewTopStatsAds, setAds, seq);
    }

    useEffect(() => {
        if (!accountIds.length) {
            requestSeq.current += 1;
            setGeneral(initialBlock(false));
            setProfit(initialBlock(false));
            setAds(initialBlock(false));
            return;
        }
        loadStats();
    }, [accountKey, periodKey]);

    return (
        <DashboardKpi
            cards={cards}
            loading={loading}
            error={error}
            comparisonLabel={comparisonLabel}
            hideComparison={hideComparison}
        />
    );
}
